import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ToasterService } from '../../../../../core/services/toaster.service';
import { DateUtil } from '../../../../../core/utils/date-util';
import { LocalStorageUtil } from '../../../../../core/utils/local-storage-util';
import { ModalBaseClass } from '../../../../shared/components/modals/base_class/modal-base-class';
import { MasterService } from '../../../../../state/master/services/master.service';
import { ResourceApiError } from '../../../../../state/modules/resource/resource-response.util';
import { AdvanceTaskService } from '../../../../../state/modules/advance-task/services/advance-task.service';
import {
  AdvanceTaskCompletionDto,
  AdvanceTaskMyTasksTodayDto,
  AdvanceTaskOnShiftGroupDto,
  AdvanceTaskOnShiftSectionDto,
  AdvanceTaskOnShiftTaskDto,
} from '../../../../../state/modules/advance-task/types/advance-task.types';
import { ACTIVITY_TABS } from '../../../client/pages/client-info/pages/location/components/location-advance-task/advance-task.constant';
import { FrequencyTab } from '../../../client/pages/client-info/pages/location/components/location-advance-task/advance-task.model';

import {
  ALREADY_TICKED_MESSAGE,
  FREQUENCY_SHORT,
  GROUP_NAME_MAX_CHARS,
  NO_ACCESS_OTHERS_TASKS,
  NO_SHIFT_NO_RECORD,
  NO_TASKS_NO_RECORD,
  STANDALONE_SECTION_LABEL,
  TASK_NAME_MAX_CHARS,
  PRIORITY_COLOUR,
} from './advance-task-activity.constant';
import { TaskCommentsComponent } from './task-comments/task-comments.component';

@Component({
  standalone: false,
  selector: 'kt-advance-task-activity',
  templateUrl: './advance-task-activity.component.html',
  styleUrls: ['./advance-task-activity.component.scss'],
})
export class AdvanceTaskActivityComponent extends ModalBaseClass implements OnInit {

  /** Set by the caller: whose feed to show. 0 = the logged-in user's own. */
  userId = 0;
  /**
   * Set by the caller when the feed may be read but not changed — e.g. someone else's feed, since tick and
   * comment always act as the logged-in user.
   */
  readOnly = false;
  /** Why the feed is read only, shown on the banner and on every tick's tooltip. */
  readOnlyReason = '';

  /** Same tabs as the Summary page: Today (everything for today), then by frequency. */
  tabs = ACTIVITY_TABS;
  /** The business's own words for Location / Area, for the scope badge. */
  businessLabel = MasterService.masterBusinessLabel;
  noShiftNoRecord = NO_SHIFT_NO_RECORD;
  noTasksNoRecord = NO_TASKS_NO_RECORD;
  noAccessRecord = NO_ACCESS_OTHERS_TASKS;
  /** Another employee's feed refused (204): only a System Admin or Location Manager may see it. */
  noAccess = signal(false);
  groupNameMax = GROUP_NAME_MAX_CHARS;
  taskNameMax = TASK_NAME_MAX_CHARS;
  standaloneLabel = STANDALONE_SECTION_LABEL;

  loading = signal(false);
  feed = signal<AdvanceTaskMyTasksTodayDto>(null);
  activeTab = signal<FrequencyTab>('ALL');
  collapsedGroups = signal<number[]>([]);
  /** Ids being ticked, so a row cannot be clicked twice while its call is in flight. */
  busyTaskIds = signal<number[]>([]);

  private advanceTask = inject(AdvanceTaskService);
  private toaster = inject(ToasterService);
  private loggedInUserId = LocalStorageUtil.getUserId();

  groups = computed<AdvanceTaskOnShiftGroupDto[]>(() => {
    const data = this.feed();

    return data && data.groups ? data.groups : [];
  });

  hasOpenShift = computed(() => !!this.feed() && this.feed().has_open_shift);

  private allTasks = computed<AdvanceTaskOnShiftTaskDto[]>(() => {
    const tasks: AdvanceTaskOnShiftTaskDto[] = [];
    this.groups().forEach(group => (group.sections || [])
      .forEach(section => (section.tasks || []).forEach(task => tasks.push(task))));

    return tasks;
  });

  /** Only today's tasks are shown, counted and tickable — the same set the Summary page shows for a day. */
  todayTasks = computed(() => this.allTasks().filter(task => this.isForToday(task)));

  doneTodayCount = computed(() => this.todayTasks().filter(task => task.is_done).length);

  progressPercent = computed(() => {
    const total = this.todayTasks().length;

    return total ? Math.round(this.doneTodayCount() * 100 / total) : 0;
  });

  constructor(public activeModal: NgbActiveModal, public modalService: NgbModal) {
    super(modalService);
  }

  /** A task's comments for the shift's own day; comments never tick a task, only its comment count changes. */
  commentsClick(task: AdvanceTaskOnShiftTaskDto) {
    const dueDate = this.feed() ? this.feed().due_date : null;
    if (!dueDate) {
      return;
    }
    this.openPopUp(TaskCommentsComponent, {
      taskId: task.advance_task_id,
      taskName: task.advance_task_name,
      dueDate,
      locationId: this.feed().location_id,
      userId: this.userId,
      readOnly: this.readOnly,
      readOnlyReason: this.readOnlyReason,
      onCountChange: (count: number) => this.setCommentCount(task.advance_task_id, count),
    }, { centered: true, size: 'lg', backdrop: 'static' });
  }

  ngOnInit() {
    this.loadFeed();
  }

  private loadFeed() {
    this.loading.set(true);
    this.noAccess.set(false);
    this.advanceTask.getMyTasksToday(this.userId).subscribe({
      next: data => {
        this.loading.set(false);
        this.feed.set(data);
      },
      error: (error: ResourceApiError) => {
        this.loading.set(false);
        this.feed.set(null);
        // 204 on someone else's feed = not allowed (not an error): say so in the panel instead of a toast.
        if (error.status === 204 && this.userId) {
          this.noAccess.set(true);
          return;
        }
        this.toaster.errorShow(error.message);
      },
    });
  }

  refresh() {
    this.loadFeed();
  }

  tabCount(tab: FrequencyTab): number {
    return this.todayTasks().filter(task => tab === 'ALL' || task.advance_task_frequency === tab).length;
  }

  setTab(tab: FrequencyTab) {
    this.activeTab.set(tab);
  }

  /**
   * Same rule as the Summary page's by-date API: a scheduled task is for today when the API marks it due today;
   * an On request task always is (the API already drops ones done on an earlier day).
   */
  private isForToday(task: AdvanceTaskOnShiftTaskDto): boolean {
    return task.is_due_today || task.advance_task_frequency === 'ON_REQUEST';
  }

  /** All = every task for today; the other tabs narrow that by the task's own frequency. */
  private inActiveTab(task: AdvanceTaskOnShiftTaskDto): boolean {
    const tab = this.activeTab();
    return tab === 'ALL' || task.advance_task_frequency === tab;
  }

  visibleTasks(section: AdvanceTaskOnShiftSectionDto): AdvanceTaskOnShiftTaskDto[] {
    return (section.tasks || []).filter(task => this.isForToday(task) && this.inActiveTab(task));
  }

  visibleSections(group: AdvanceTaskOnShiftGroupDto): AdvanceTaskOnShiftSectionDto[] {
    const sections = (group.sections || []).filter(section => this.visibleTasks(section).length > 0);
    // Tasks without a section first: listed after the last section, they'd look like part of it.
    return [
      ...sections.filter(section => section.advance_task_section_id === null),
      ...sections.filter(section => section.advance_task_section_id !== null),
    ];
  }

  visibleGroups(): AdvanceTaskOnShiftGroupDto[] {
    return this.groups().filter(group => this.visibleSections(group).length > 0);
  }

  /** Done out of the tasks the section shows in this tab. */
  sectionDoneCount(section: AdvanceTaskOnShiftSectionDto): number {
    return this.visibleTasks(section).filter(task => task.is_done).length;
  }

  groupDoneCount(group: AdvanceTaskOnShiftGroupDto): number {
    return this.groupDueTasks(group).filter(task => task.is_done).length;
  }

  groupDueCount(group: AdvanceTaskOnShiftGroupDto): number {
    return this.groupDueTasks(group).length;
  }

  private groupDueTasks(group: AdvanceTaskOnShiftGroupDto): AdvanceTaskOnShiftTaskDto[] {
    const tasks: AdvanceTaskOnShiftTaskDto[] = [];
    (group.sections || []).forEach(section => (section.tasks || [])
      .forEach(task => {
        if (this.isForToday(task)) {
          tasks.push(task);
        }
      }));

    return tasks;
  }

  isCollapsed(group: AdvanceTaskOnShiftGroupDto): boolean {
    return this.collapsedGroups().indexOf(group.advance_task_group_id) > -1;
  }

  toggleGroup(group: AdvanceTaskOnShiftGroupDto) {
    const id = group.advance_task_group_id;
    this.collapsedGroups.update(ids => ids.indexOf(id) > -1
      ? ids.filter(current => current !== id)
      : [...ids, id]);
  }

  /** "A very long name…" when over max characters; the full name is always in the tooltip. */
  shorten(name: string, max: number): string {
    const text = (name || '').trim();
    return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
  }

  frequencyLabel(task: AdvanceTaskOnShiftTaskDto): string {
    return FREQUENCY_SHORT[task.advance_task_frequency] || '';
  }

  priorityColour(task: AdvanceTaskOnShiftTaskDto): string {
    return PRIORITY_COLOUR[task.advance_task_priority] || '';
  }

  guidanceTimeLabel(task: AdvanceTaskOnShiftTaskDto): string {
    return task.advance_task_guidance_time || '';
  }

  completedLabel(task: AdvanceTaskOnShiftTaskDto): string {
    if (!task.is_done) {
      return '';
    }
    const name = task.completed_by_user_id === this.loggedInUserId
      ? 'You'
      : task.completed_by_user_name || '';
    const at = DateUtil.utcToBusinessDateTime(task.completed_at);

    return at ? `${name} · ${at}` : name;
  }

  /** Only the person who ticked a task may undo it, on shared groups too. */
  canUntick(task: AdvanceTaskOnShiftTaskDto): boolean {
    return task.is_done && task.completed_by_user_id === this.loggedInUserId;
  }

  canTick(task: AdvanceTaskOnShiftTaskDto): boolean {
    if (this.readOnly || !this.isForToday(task) || this.isBusy(task)) {
      return false;
    }

    return task.is_done ? this.canUntick(task) : true;
  }

  isBusy(task: AdvanceTaskOnShiftTaskDto): boolean {
    return this.busyTaskIds().indexOf(task.advance_task_id) > -1;
  }

  tickTooltip(task: AdvanceTaskOnShiftTaskDto): string {
    if (this.readOnly) {
      if (this.readOnlyReason) {
        return this.readOnlyReason;
      }

      return task.is_done ? `Completed by ${task.completed_by_user_name || 'a team member'}` : 'Not done yet';
    }
    if (task.is_done) {
      return this.canUntick(task)
        ? 'Undo'
        : `Completed by ${task.completed_by_user_name || 'another team member'}`;
    }

    return 'Mark as done';
  }

  toggleTask(task: AdvanceTaskOnShiftTaskDto) {
    if (!this.canTick(task)) {
      return;
    }
    const dueDate = this.feed().due_date;
    if (!dueDate) {
      return;
    }
    this.setBusy(task, true);
    const request = task.is_done
      ? this.advanceTask.untickTask(task.advance_task_id, dueDate)
      : this.advanceTask.tickTask(task.advance_task_id, dueDate);

    request.subscribe({
      next: result => {
        this.setBusy(task, false);
        this.applyResult(task, result);
      },
      error: (error: ResourceApiError) => {
        this.setBusy(task, false);
        if (error.status === 409) {
          this.toaster.infoShow(ALREADY_TICKED_MESSAGE);
          this.loadFeed();

          return;
        }
        this.toaster.errorShow(error.message);
      },
    });
  }

  /** The row is updated from the response rather than reloading the whole feed for one tick. */
  private applyResult(task: AdvanceTaskOnShiftTaskDto, result: AdvanceTaskCompletionDto) {
    const done = !!result.is_done;
    this.updateTask(task.advance_task_id, row => ({
      ...row,
      is_done: done,
      completed_by_user_id: done ? this.loggedInUserId : null,
      completed_by_user_name: done ? row.completed_by_user_name : null,
      completed_by_profile_url: done ? row.completed_by_profile_url : null,
      completed_at: done ? result.completed_at : null,
    }));
  }

  /** The comments pop-up reports its count after loading, posting or deleting, so the row's number stays right. */
  private setCommentCount(taskId: number, count: number) {
    this.updateTask(taskId, row => (row.comment_count === count ? row : { ...row, comment_count: count }));
  }

  /**
   * Replaces one task row. New objects down to the row (not edits in place), so groups() changes and the
   * progress bar and counts recalculate.
   */
  private updateTask(taskId: number, change: (row: AdvanceTaskOnShiftTaskDto) => AdvanceTaskOnShiftTaskDto) {
    this.feed.update(data => data && {
      ...data,
      groups: (data.groups || []).map(group => ({
        ...group,
        sections: (group.sections || []).map(section => ({
          ...section,
          tasks: (section.tasks || []).map(row => (row.advance_task_id === taskId ? change(row) : row)),
        })),
      })),
    });
  }

  private setBusy(task: AdvanceTaskOnShiftTaskDto, busy: boolean) {
    const id = task.advance_task_id;
    this.busyTaskIds.update(ids => busy
      ? [...ids, id]
      : ids.filter(current => current !== id));
  }

  trackGroup(index: number, group: AdvanceTaskOnShiftGroupDto): number {
    return group.advance_task_group_id;
  }

  trackSection(index: number, section: AdvanceTaskOnShiftSectionDto): number {
    return section.advance_task_section_id === null ? -1 : section.advance_task_section_id;
  }

  trackTask(index: number, task: AdvanceTaskOnShiftTaskDto): number {
    return task.advance_task_id;
  }
}
